import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, 
  Clock, 
  Layers,
  ChevronLeft,
  Search,
  Filter,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import PublicFooter from '../components/PublicFooter';
import SEO, { PageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PublicNav() {
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/public/courses" className="text-[#1E3A5F] font-medium">
              Courses
            </Link>
            <Link to="/public/schedule" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Schedule
            </Link>
            <Link to="/public/instructors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Instructors
            </Link>
            <Link to="/public/sponsors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Become a Sponsor
            </Link>
          </div>
          
          <Link to="/public/request">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white">
              Book for Your Team
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function PublicCoursesPage() {
  const { courseId } = useParams();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [hoursFilter, setHoursFilter] = useState('all');
  const [courseTypeFilter, setCourseTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (courseId && courses.length > 0) {
      const course = courses.find(c => c.id === courseId);
      setSelectedCourse(course);
    } else {
      setSelectedCourse(null);
    }
  }, [courseId, courses]);

  useEffect(() => {
    filterCourses();
  }, [courses, searchTerm, categoryFilter, hoursFilter, courseTypeFilter]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/courses`);
      setCourses(response.data);
      setFilteredCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCourses = () => {
    let filtered = [...courses];
    
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(c => c.category === categoryFilter);
    }
    
    if (hoursFilter !== 'all') {
      filtered = filtered.filter(c => c.credit_hours === parseInt(hoursFilter));
    }
    
    if (courseTypeFilter !== 'all') {
      filtered = filtered.filter(c => {
        const type = c.course_type || 'ce';
        return courseTypeFilter === 'ce' ? type === 'ce' : type === 'professional_development';
      });
    }
    
    setFilteredCourses(filtered);
  };

  const categories = [...new Set(courses.map(c => c.category).filter(Boolean))];
  const hours = [...new Set(courses.map(c => c.credit_hours))].sort((a, b) => a - b);
  
  // Helper to check if course is CE
  const isCECourse = (course) => !course.course_type || course.course_type === 'ce';

  if (selectedCourse) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/public/courses" className="inline-flex items-center text-slate-600 hover:text-[#1E3A5F] mb-6">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Courses
          </Link>
          
          <Card className="border-slate-200 overflow-hidden">
            <div className="h-3" style={{ backgroundColor: selectedCourse.color || '#1E3A5F' }}></div>
            <CardContent className="pt-8">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${isCECourse(selectedCourse) ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                  {selectedCourse.credit_hours} {isCECourse(selectedCourse) ? 'CE Hour' : 'Hour'}{selectedCourse.credit_hours > 1 ? 's' : ''}
                </span>
                <span className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                  {selectedCourse.category || 'General'}
                </span>
                {isCECourse(selectedCourse) ? (
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                    TREC Approved
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                    Professional Training
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {selectedCourse.title}
              </h1>
              
              <p className="text-slate-600 text-lg mb-8">
                {selectedCourse.description}
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <Clock className="w-6 h-6 text-sky-500" />
                  <div>
                    <p className="text-sm text-slate-500">Duration</p>
                    <p className="font-semibold text-[#1E3A5F]">{selectedCourse.credit_hours} Hour{selectedCourse.credit_hours > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <Layers className="w-6 h-6 text-sky-500" />
                  <div>
                    <p className="text-sm text-slate-500">Content</p>
                    <p className="font-semibold text-[#1E3A5F]">{selectedCourse.total_slides} Slides</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <BookOpen className="w-6 h-6 text-sky-500" />
                  <div>
                    <p className="text-sm text-slate-500">Format</p>
                    <p className="font-semibold text-[#1E3A5F]">Webinar or In-Person</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-semibold text-[#1E3A5F] mb-4">Interested in this course?</h3>
                <div className="flex flex-wrap gap-4">
                  <Link to="/public/schedule">
                    <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                      View Upcoming Sessions
                    </Button>
                  </Link>
                  <Link to={`/public/request?course=${selectedCourse.id}`}>
                    <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                      Request for Your Team
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...PageSEO.courses} />
      <PublicNav />
      
      {/* Header */}
      <section className="bg-[#1E3A5F] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Course Catalog
          </h1>
          <p className="text-sky-200 text-lg">
            Browse our complete library of continuing education and professional training courses
          </p>
        </div>
      </section>
      
      {/* Filters */}
      <section className="bg-white border-b border-slate-200 py-4 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={courseTypeFilter} onValueChange={setCourseTypeFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Award className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Course Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                <SelectItem value="ce">CE Courses (TREC)</SelectItem>
                <SelectItem value="training">Training Courses</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={hoursFilter} onValueChange={setHoursFilter}>
              <SelectTrigger className="w-full md:w-40">
                <Clock className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Hours" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hours</SelectItem>
                {hours.map(h => (
                  <SelectItem key={h} value={h.toString()}>{h} Hour{h > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>
      
      {/* Course Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-slate-600 mb-6">
            Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
          </p>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-slate-500 mt-4">Loading courses...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No courses found matching your criteria</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCourses.map((course) => {
                const isCE = isCECourse(course);
                return (
                  <Card key={course.id} className="border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`course-card-${course.id}`}>
                    <div className="h-2" style={{ backgroundColor: course.color || '#1E3A5F' }}></div>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {isCE ? (
                          <>
                            <span className="px-2 py-1 bg-sky-100 text-sky-700 text-xs font-medium rounded" data-testid="course-type-badge">
                              {course.credit_hours} CE Hour{course.credit_hours > 1 ? 's' : ''}
                            </span>
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded" data-testid="trec-badge">
                              TREC Approved
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded" data-testid="course-type-badge">
                              {course.credit_hours} Hour{course.credit_hours > 1 ? 's' : ''}
                            </span>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded" data-testid="training-badge">
                              Training
                            </span>
                          </>
                        )}
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                          {course.category || 'General'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#1E3A5F] mb-2 line-clamp-2">{course.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-3 mb-4">{course.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">
                          {course.total_slides} slides
                        </span>
                        <Link to={`/public/courses/${course.id}`}>
                          <Button variant="outline" size="sm" data-testid={`view-details-${course.id}`}>
                            View Details
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA */}
      <section className="bg-[#1E3A5F] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Want to bring these courses to your team?
          </h2>
          <p className="text-sky-200 mb-6">
            We offer private sessions for brokerages and teams with flexible scheduling.
          </p>
          <Link to="/public/request">
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white">
              Request a Course for Your Team
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
